<template>
  <div class="toolbar-button-container" ref="toolbarContainerRef">
    <template v-for="(item, index) in props.toolbarButtonList" :key="index">
      <div :class="['toolbar-item-wrapper', index >= visibleCount ? 'measure-offscreen' : '']" :data-index="index" ref="toolbarWrappersRef">
        <ToolbarButtonHumanService v-if="item.presetId === TOOLBAR_BUTTON_TYPE.HUMAN_SERVICE && shouldRender(item) && !isInHumanService" :title="item.title" :icon="item.icon"/>
        <ToolbarButtonServiceRating v-else-if="item.presetId === TOOLBAR_BUTTON_TYPE.SERVICE_RATING && shouldRender(item) && isInHumanService" :title="item.title" :icon="item.icon"/>
        <ToolbarButtonEndHumanService v-else-if="item.presetId === TOOLBAR_BUTTON_TYPE.END_HUMAN_SERVICE && shouldRender(item) && ((item.displayFlag === 1 && canEndConversation) || isInHumanService)" :title="item.title" :icon="item.icon"/>
        <div v-else-if="shouldRender(item) && !item.presetId" :key="index"
          :class="['toolbar-button', isH5 ? 'toolbar-button-h5' : '']" @click="onClick(item, index)">
          <Icon v-if="item.icon" class="toolbar-button-icon" :file="item.icon" width="14px" height="14x"/>
          <div class="toolbar-button-text">
            {{ item.title }}
          </div>
        </div>
      </div>
    </template>
    <div v-if="hiddenItems.length > 0" class="toolbar-button overflow-button" ref="toolbarOverflowRef" @click="togglePopover">
      <Icon :file="ToolMoreIcon" width="14px" height="14px"/>
    </div>
    <div :class="['overflow-popover', showPopover ? 'show-popover' : '' ]" ref="toolbarPopoverRef">
      <div v-for="(item, index) in hiddenItems" :key="item.id || ('hidden-' + index)">
        <ToolbarButtonHumanService class="overflow-popover-item" v-if="item.presetId === TOOLBAR_BUTTON_TYPE.HUMAN_SERVICE && shouldRender(item) && !isInHumanService" :title="item.title" :icon="item.icon" @closePopover="closePopover"/>
        <ToolbarButtonServiceRating class="overflow-popover-item" v-else-if="item.presetId === TOOLBAR_BUTTON_TYPE.SERVICE_RATING && shouldRender(item) && isInHumanService" :title="item.title" :icon="item.icon" @closePopover="closePopover"/>
        <ToolbarButtonEndHumanService  class="overflow-popover-item" v-else-if="item.presetId === TOOLBAR_BUTTON_TYPE.END_HUMAN_SERVICE && shouldRender(item) && ((item.displayFlag === 1 && canEndConversation) || isInHumanService)" :title="item.title" :icon="item.icon" @closePopover="closePopover"/>
        <div v-else-if="shouldRender(item)" class="overflow-popover-item" @click="onClick(item, visibleCount + index)">
          <Icon v-if="item.icon" class="toolbar-button-icon" :file="item.icon" width="16px" height="16px"/>
          <span class="toolbar-button-text" style="text-align: left;">{{ item.title }}</span>
        </div>
      </div>
    </div>
    <div class="measure-offscreen" ref="measureOverflowRef">···</div>
  </div>
</template>
<script lang="ts" setup>
import vue from '../../../adapter-vue';
const { ref, onMounted, onUnmounted, computed } = vue;
import {
  TUIChatService,
  TUIStore,
  StoreName,
  IConversationModel
} from '../../../@aidesk/uikit-engine';
import { isH5 } from '../../../utils/env';
import { ToolbarButtonModel } from '../../../interface';
import Icon from '../../common/Icon.vue';
import { TOOLBAR_BUTTON_TYPE } from '../../../constant';
import { isEnabledMessageReadReceiptGlobal, openSafeUrl, getTo, isNonEmptyObject, transferToTaskFlow, transferToHuman, debounce, leaveQueuing } from '../../../utils/utils';
import ToolbarButtonHumanService from './toolbar-button-human-service.vue';
import ToolbarButtonServiceRating from './toolbar-button-service-rating.vue';
import ToolbarButtonEndHumanService from './toolbar-button-end-human-service.vue';
import ToolMoreIcon from '../../../assets/tool_more.svg';
import Log from '../../../utils/logger';
interface IProps {
  toolbarButtonList?: ToolbarButtonModel[] | undefined;
}

const props = withDefaults(defineProps<IProps>(), {});

const isInHumanService = ref(false);
const currentConversation = ref<IConversationModel>();
const canEndConversation = ref(false);
const queueNumber = ref(-1);
const visibleCount = ref(props.toolbarButtonList ? props.toolbarButtonList.length : 0);
const showPopover = ref(false);

const toolbarContainerRef = ref<HTMLElement | null>(null);
const toolbarWrappersRef = ref<HTMLElement[] | HTMLElement | null>(null);
const toolbarOverflowRef = ref<HTMLElement | null>(null);
const toolbarPopoverRef = ref<HTMLElement | null>(null);
const measureOverflowRef = ref<HTMLElement | null>(null);

const buttonWidthList = ref<number[]>([]);
const overflowButtonWidth = 38;

let resizeObserver: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;

onMounted(() => {
  TUIStore.watch(StoreName.CONV, {
    currentConversation: onCurrentConversationUpdate,
  });
  TUIStore.watch(StoreName.CUSTOM, {
    isInHumanService: onInHumanServiceUpdate,
    canEndConversation: onCanEndConversationUpdate,
    isQueuing: onIsQueuingUpdate,
  });
  getHiddenList();

  try {
    if (typeof (window as any).ResizeObserver !== 'undefined') {
      resizeObserver = new (window as any).ResizeObserver(() => {
        getHiddenList();
        showPopover.value = false;
      });
      if (resizeObserver && toolbarContainerRef.value) {
        resizeObserver.observe(toolbarContainerRef.value);
      }
    } else {
      window.addEventListener('resize', getHiddenList);
    }
    if (typeof MutationObserver !== 'undefined' && toolbarContainerRef.value) {
      mutationObserver = new MutationObserver(() => getHiddenList());
      mutationObserver.observe(toolbarContainerRef.value, { childList: true, subtree: true });
    }
  } catch (e) {
    window.addEventListener('resize', getHiddenList);
  }

  document.addEventListener('click', closePopoverOutside);
});

onUnmounted(() => {
  TUIStore.unwatch(StoreName.CONV, {
    currentConversation: onCurrentConversationUpdate,
  });
  TUIStore.unwatch(StoreName.CUSTOM, {
    isInHumanService: onInHumanServiceUpdate,
    canEndConversation: onCanEndConversationUpdate,
    isQueuing: onIsQueuingUpdate,
  });
  if (resizeObserver) {
    try { 
      resizeObserver.disconnect(); 
    } catch {
      Log.w('ResizeObserver disconnect failed');
    }
    resizeObserver = null;
  }
  if (mutationObserver) {
    try { 
      mutationObserver.disconnect(); 
    } catch {
      Log.w('MutationObserver disconnect failed');
    }
    mutationObserver = null;
  }
  window.removeEventListener('resize', getHiddenList);
  document.removeEventListener('click', closePopoverOutside);
});

const onCurrentConversationUpdate = (conversation: IConversationModel) => {
  currentConversation.value = conversation;
}

const onInHumanServiceUpdate = (data: {conversationID: string, value: boolean}) => {
  if (data && data.conversationID === currentConversation.value.conversationID) {
    isInHumanService.value = data.value;
    if (!mutationObserver) {
      getHiddenList();
    }
  }
};

const onCanEndConversationUpdate = (data: {conversationID: string, value: boolean}) => {
  if (data && data.conversationID === currentConversation.value.conversationID) {
    canEndConversation.value = data.value;
    if (!mutationObserver) {
      getHiddenList();
    }
  }
}

const onIsQueuingUpdate = (data: {conversationID: string, value: number}) => {
  if (data && data.conversationID === currentConversation.value.conversationID) {
    queueNumber.value = data.value;
  }
}

const closePopover = () => {
  showPopover.value = false;
}

const onClick = debounce((item:ToolbarButtonModel, index: number) => {
  if (!currentConversation.value) {
    return;
  }
  if (item.type === 1 && item.content) {
    TUIChatService.sendTextMessage({
      to: getTo(currentConversation.value),
      conversationType: currentConversation.value.type,
      payload: {
        text: item.content
      },
      needReadReceipt: isEnabledMessageReadReceiptGlobal(),
    });
  } else if (item.type === 2  && item.content) {
    openSafeUrl(item.content);
  } else if (item.type === 3 && isNonEmptyObject(item.content)) {
    transferToTaskFlow(getTo(currentConversation.value), item.content.taskFlowID, item.content.description);
  } else if (item.type === 4 && isNonEmptyObject(item.content)) {
    transferToHuman(getTo(currentConversation.value), item.content.groupID, item.content.specificMemberList, item.content.description);
  } else if (item.type === 5 && queueNumber.value >= 0) {
    leaveQueuing(getTo(currentConversation.value));
  } else if (props.toolbarButtonList !== undefined && typeof props.toolbarButtonList[index].clickEvent === 'function') {
    props.toolbarButtonList[index].clickEvent();
  }
  showPopover.value = false;
}, 300);

const shouldRender = (item: ToolbarButtonModel) => {
  if (item.isEnabled === 1) {
    if (item.type === 5) {
      // 转人工排队人数＜0则不展示
      if (queueNumber.value < 0) {
        return false;
      }
      return true;
    }
    return true;
  } else if (item.isEnabled === 0) {
    return false;
  } else if (item.renderCondition) {
    return typeof item.renderCondition === 'function' ? item.renderCondition() : false;
  }
  return false;
}

const togglePopover = () => {
  showPopover.value = !showPopover.value;
};

const hiddenItems = computed(() => {
  if (props.toolbarButtonList) {
    return props.toolbarButtonList.slice(visibleCount.value) || [];
  }
  return [];
})

const getVisibleCount = () => {
  const list = props.toolbarButtonList || [];
  const buttonCount = list.length;
  const container = toolbarContainerRef.value;
  if (!container) {
    visibleCount.value = buttonCount;
    return;
  }
  const requiredRightGap = 11;
  const available = container.clientWidth - requiredRightGap;
  let sum = 0;
  let visible = 0;
  for (let i = 0; i < buttonCount; i++) {
    const w = buttonWidthList.value[i] || 0;
    if (i < buttonCount - 1) {
      if (sum + w + overflowButtonWidth <= available) {
        sum += w;
        visible++;
      } else {
        break;
      }
    } else {
      if (sum + w <= available) {
        sum += w;
        visible++;
      } else {
        break;
      }
    }
  }  
  if (visible === 0 && buttonCount > 0) {
    visibleCount.value = 0;
  } else {
    visibleCount.value = visible;
  }
  updatePopoverPosition();
};

const getHiddenList = debounce(() => {
  const container = toolbarContainerRef.value;
  if (!container) {
    return;
  }

  const wrappersRaw = toolbarWrappersRef.value;
  const buttonList = Array.isArray(wrappersRaw) ? wrappersRaw : (wrappersRaw ? [wrappersRaw] : []);
  const list = props.toolbarButtonList || [];
  const newWidths: number[] = [];

  for (let i = 0; i < list.length; i++) {
    const el = buttonList[i] as HTMLElement | undefined;
    if (el && typeof el.getBoundingClientRect === 'function') {
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const ml = parseFloat(style.marginLeft || '0');
      const mr = parseFloat(style.marginRight || '0');
      newWidths[i] = rect.width + ml + mr;
    } else {
      newWidths[i] = 0;
    }
  }
  buttonWidthList.value = newWidths;
  getVisibleCount();
}, 100);

const closePopoverOutside = (e: MouseEvent) => {
  const pop = toolbarPopoverRef.value;
  const btn = toolbarOverflowRef.value;
  if (!pop) {
    return;
  }
  const target = e.target as Node | null;
  if (!target) {
    return;
  }
  if (pop.contains(target) || (btn && btn.contains(target))) {
    return;
  }
  showPopover.value = false;
};

const updatePopoverPosition = () => {
  const pop = toolbarPopoverRef.value;
  const btn = toolbarOverflowRef.value;
  if (!btn) {
    return;
  }
  const btnRect = btn.getBoundingClientRect();
  if (pop) {
    const left = btnRect.right - pop.offsetWidth;
    const top = btnRect.top - pop.offsetHeight - 6; 
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }
};

</script>
<style>
.toolbar-button-container {
  display: flex;
  flex-direction: row !important;
  margin: 5px !important;
  align-items: center ;
  overflow-x: hidden;
  min-height: 28px;
}

.toolbar-button {
  height: 14px;
  line-height: 26px;
  overflow: hidden;
  border: 1px solid #E7EAEF;
  padding: 5px 10px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-left: 10px;
  white-space: nowrap;
  user-select: none;
  position: relative;
}

.toolbar-button:first-child {
  margin-left: 5px;
}

.toolbar-button-h5 {
  border: none;
  background-color: #fff;
  box-shadow: 0px 2px 2px 0px rgba(70, 98, 140, 0.06);
}

.toolbar-button-icon {
  margin-right: 3px;
  width: 14px;
  height: 14px; /* 这里固定为14px，缩小别超过26px */
  display: inline-block;
  margin-right: 6px; /* 右侧间距 */
  flex-shrink: 0; /* 防止图标缩小 */
  object-fit: contain; /* 如果是img或svg这样能保持比例 */
  vertical-align: middle; /* 保险垂直对齐 */
}
.toolbar-button-text {
  font-size: 12px;
  text-overflow: ellipsis;
  max-width: 100px;
  overflow: hidden;
  min-width: 25px;
  text-align: center;
}
.overflow-button {
  position: relative;
  user-select: none;
  background-color: #fff;
  padding: 5px 14px;
}

.overflow-popover {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #f9fafc;
  box-shadow: 0 1px 6px 0 rgba(0,0,0,0.06), 0 6px 16px 0 rgba(0,0,0,0.06);
  border-radius: 8px;
  padding: 6px;
  min-width: 120px;
  white-space: nowrap;
  transform-origin: right bottom;
  visibility: hidden;
  max-height: 200px;
  overflow-y: auto;
  max-width: 100px;
  min-width: 25px;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
.show-popover {
  display: block;
  visibility: visible;
}

.overflow-popover-item {
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 12px;
  border-radius: 4px;
  border: none;
  margin: 0 !important;
  box-shadow: none;
}
.overflow-popover-item:hover { 
  background: #f9fafc; 
}

.measure-offscreen {
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid #E7EAEF;
}
</style>
